import { RawInput } from "@/ui";
import { useState } from "react";
import { AccountLabel } from "@/entities/DataAssociation/Account.model";
import { ACCOUNT_LIST } from "../labeling.constants";
import clsx from "clsx";
import { LabelList } from "../components/LabelList";
import { useAtomValue, useSetAtom } from "jotai";
import {
    accountNamingErrorMessageAtom,
    accountsAtom,
    labelingContextViewAtom,
    NormalizedAccount,
} from "../state";
import SearchBar from "@/features/Navigation/components/SearchBar";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import { Label } from "@/ui/organisms/Form/Label";
import { LabelItem } from "../components/LabelItem";
import { XCircleIcon } from "@heroicons/react/24/outline";

export function AccountEditForm({ initial }: { initial: NormalizedAccount }) {
    const setContextView = useSetAtom(labelingContextViewAtom);
    const setAccounts = useSetAtom(accountsAtom);
    const [accountName, setAccountName] = useState<string | null>(
        initial.name || null,
    );
    const [selectedLabel, setAccountLabel] = useState<AccountLabel | null>(
        initial.label,
    );
    const [searchTerm, setSearchTerm] = useState<string>("");
    const getErrorMessage = useAtomValue(accountNamingErrorMessageAtom);
    const errorMessage = accountName
        ? getErrorMessage(accountName, {
              currentAccountId: initial.id || undefined,
          })
        : undefined;
    const hasChanges =
        accountName !== initial.name || selectedLabel !== initial.label;

    return (
        <div
            className={clsx(
                "absolute inset-0 flex min-h-0 flex-col overflow-hidden",
            )}
        >
            <div className={clsx("mb-3 mt-6 px-6", "flex flex-col", "sticky")}>
                <RightSidebarButton
                    label="Accounts"
                    iconDirection="left"
                    onClick={() =>
                        setContextView({ type: "ACCOUNT_LIST_EDIT" })
                    }
                    useEscapeToClose={true}
                />
                <h2 className="pageheading">Edit Account</h2>
                <p className="body-s subdued mb-6">
                    Changes will be applied to this account across all files
                    upon commit of the draft.
                </p>
                <RawInput
                    name="accountName"
                    label="Account Name"
                    value={accountName || ""}
                    showErrorRing={!!errorMessage}
                    onChange={(e) => setAccountName(e.target.value)}
                />
                {errorMessage && (
                    <p className="ui-element mt-1 text-red-600">
                        {errorMessage}
                    </p>
                )}
            </div>
            <div className="flex flex-col gap-2 px-6">
                <div className="flex items-center justify-between gap-2">
                    <Label name="label" label="Label" />
                    {selectedLabel !== initial.label && (
                        <button
                            className="quiet subdued ui-label p-0"
                            onClick={() => setAccountLabel(initial.label)}
                        >
                            Clear
                            <XCircleIcon className="size-4" />
                        </button>
                    )}
                </div>
                {ACCOUNT_LIST[selectedLabel || initial.label] && (
                    <LabelItem
                        dot
                        listItem
                        label={ACCOUNT_LIST[selectedLabel || initial.label]}
                        className={clsx("selected", "mb-2")}
                    />
                )}
                <SearchBar
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search labels..."
                />
            </div>
            <div className="custom-scrollbar overflow-y-auto px-6">
                <LabelList
                    className="mt-0 pb-24"
                    labels={Object.values(ACCOUNT_LIST)
                        .filter((label) => {
                            if (label.code === initial.label) return false;
                            if (!searchTerm) return true;
                            const term = searchTerm.toLowerCase();
                            return (
                                label.category.toLowerCase().includes(term) ||
                                label.name.toLowerCase().includes(term) ||
                                label.code.toLowerCase().includes(term) ||
                                label.statementType.toLowerCase().includes(term)
                            );
                        })
                        .sort((a, b) => {
                            if (initial?.label) {
                                if (a.code === initial.label) return -1;
                                if (b.code === initial.label) return 1;
                            }
                            return (
                                ACCOUNT_LIST[a.code].category.localeCompare(
                                    ACCOUNT_LIST[b.code].category,
                                ) || a.name.localeCompare(b.name)
                            );
                        })}
                    onLabelSelection={setAccountLabel}
                    selectedLabel={selectedLabel}
                />
            </div>

            <div
                className={clsx(
                    "bg-default w-full p-6",
                    "absolute inset-x-0 bottom-0",
                )}
            >
                <button
                    className="primary xl w-full"
                    disabled={!!errorMessage || !hasChanges}
                    onClick={() => {
                        setAccounts({
                            ...initial,
                            label:
                                (selectedLabel as AccountLabel) ||
                                initial.label,
                            name: accountName || initial.name,
                        });
                        setContextView({ type: "ACCOUNT_LIST_EDIT" });
                    }}
                >
                    Update account
                </button>
            </div>
        </div>
    );
}
